import { X } from "lucide-react";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

/**
 * Full-screen view of a single image, over a dimmed backdrop.
 *
 * For the case where a thumbnail is too small to answer the question it exists
 * to answer — "which blanket is that?", "what does the order photo actually
 * show?". Click the backdrop, press Escape, or hit the close button to dismiss.
 *
 * Promoted from `whats_new` (2026-08-25) when Major Swag needed the same thing
 * for its item photos; a second copy was the alternative. Hardened on the way:
 * it now announces as a dialog, moves focus to the close button and returns it
 * on dismiss, and locks body scroll while open — the original let the page
 * behind it scroll under the backdrop.
 *
 * Render it conditionally (`{open && <ImageLightbox …/>}`) rather than passing
 * an `open` prop: there is nothing to keep mounted between viewings.
 */
export function ImageLightbox({
  url,
  caption,
  alt,
  onClose,
  className,
}: {
  url: string;
  /** Shown under the image. Also the accessible name when `alt` is absent. */
  caption?: string;
  /** Overrides `caption` for the image's alt text — use when the caption is a
   *  sentence about the image rather than a description of it. */
  alt?: string;
  onClose: () => void;
  className?: string;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Remember what had focus so dismissing doesn't dump the user at the top
    // of the page — they came from a thumbnail and should land back on it.
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      returnFocusRef.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt ?? caption ?? "Image"}
      className={cn(
        "fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-8",
        className,
      )}
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        className="absolute right-6 top-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      <figure
        className="flex max-h-full max-w-full flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={url}
          alt={alt ?? caption ?? ""}
          className="max-h-[85vh] max-w-full rounded-md shadow-2xl"
        />
        {caption && (
          <figcaption className="max-w-xl text-center text-sm text-white/80">
            {caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
